import FsLightbox from 'fslightbox-react';
import { useEffect, useRef, useState } from 'react';
import Carousel from 'react-material-ui-carousel';

import { MEDIA_URL } from 'reservations/base/constants/bentobox';
import BookingConfirmationDetailCard from 'reservations/confirmation/components/BookingConfirmationDetailCard';

type ImageContext = {
  url: string;
  altText: string;
};

type Props = {
  context: {
    [key: string]: ImageContext;
  };
};

const ImageCarouselModule = ({ context }: Props) => {
  const [toggler, setToggler] = useState(false);
  const [image, setImage] = useState<ImageContext>({ altText: '', url: '' });
  const imageRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  const updateHeight = () => {
    const width = imageRef.current?.offsetWidth;
    width && /* istanbul ignore next */ setHeight((4 / 3) * width);
  };

  useEffect(() => {
    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => window.removeEventListener('resize', updateHeight);
  }, [imageRef]);

  const openModal = (key: string) => {
    setImage(context[key]);
    setToggler(!toggler);
  };

  return (
    <BookingConfirmationDetailCard hideDivider header="">
      <div ref={imageRef} />
      <FsLightbox
        data-testid="lightbox"
        toggler={toggler}
        sources={[`${MEDIA_URL}/${image.url}`]}
        customAttributes={[{ alt: image.altText }]}
        disableThumbs={true}
        disableLocalStorage={true}
        disableSlideSwiping={true}
        types={['image']}
      />
      <Carousel
        animation="slide"
        indicators={false}
        navButtonsAlwaysVisible={true}
        sx={{
          width: '100%',
          height: `${height}px`
        }}
      >
        {Object.entries(context)
          .filter(([_, value]) => value.url)
          .map(([key, value]) => (
            <Item
              key={key}
              imageKey={key}
              src={value.url}
              height={height}
              openModal={openModal}
            ></Item>
          ))}
      </Carousel>
    </BookingConfirmationDetailCard>
  );
};

type ItemProps = {
  src: string;
  imageKey: string;
  height: number;
  openModal: (name: string) => void;
};

function Item({ src, imageKey, openModal, height }: ItemProps) {
  const imageUrl = src.startsWith('https') ? src : `${MEDIA_URL}/${src}`;

  return (
    <div
      data-testid="image-carousel-container"
      onClick={() => openModal(imageKey)}
      style={{
        width: '100%',
        height: `${height}px`,
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center'
      }}
    ></div>
  );
}

export default ImageCarouselModule;
